<template>
  <ion-item
    :button="props.button"
    :style="rootStyles"
    @click="!disabled && props.to && router.push(props.to)"
  >
    <ion-radio v-if="props.value != null" slot="end" :value="props.value" />
    <AppStack
      direction="flex-col"
      class="group w-full"
      :class="[disabled ? 'opacity-50' : '']"
    >
      <AppStack
        justify-content="justify-center"
        direction="flex-col"
        class="relative py-3 min-h-[48px] gap-[2px]"
      >
        <AppTypography
          :color="accentClass || colorClasses?.fgPrimary"
          :class="props.accent ? ['active:opacity-50'] : []"
          name="text-md-4-medium"
        >
          {{ props.title }}
        </AppTypography>
        <AppTypography
          v-if="props.subtitle"
          :text="props.subtitle"
          :color="colorClasses?.fgSecondary"
          name="text-sm-5-normal"
        />
      </AppStack>
    </AppStack>
    <slot name="end">
      <div v-if="props.caption" slot="end">
        <AppTypography
          :color="colorClasses?.fgSecondary"
          name="text-md-4-normal"
        >
          <AppStack align-items="items-center">
            <AppLabel
              v-if="badge"
              :variant="badge.variant"
              :class="badge.variant.includes('outline') ? 'border-solid' : ''"
              class="mr-4 align-middle"
            >
              {{ badge.text }}
            </AppLabel>
            {{ props.caption }}
          </AppStack>
        </AppTypography>
      </div>
    </slot>
  </ion-item>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { IonItem, IonRadio, useIonRouter } from '@ionic/vue'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppLabel from '@restify/packages/design-system/low-level/AppLabel.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import type { ComponentProps } from '@restify/packages/types'

type Elevation = '0' | '1' | '1-ios'

const props = defineProps<{
  button?: boolean
  title: string
  subtitle?: string
  badge?: {
    text: string
    variant: NonNullable<ComponentProps<typeof AppLabel>['variant']>
  }
  value?: string
  caption?: string
  disabled?: boolean
  to?: {
    name: string
  }
  elevation?: Elevation
  accent?: 'blue' | 'red'
}>()

const router = useIonRouter()

const rootStyles = computed(
  () =>
    ({
      0: {
        '--background': 'var(--rf-bg-elevation-0)',
        '--background-activated': 'var(--rf-bg-elevation-0-alt)',
        '--background-activated-opacity': '0.5',
        '--border-color': 'var(--rf-border-elevation-0-secondary)',
      },
      1: {
        '--background': 'var(--rf-bg-elevation-1)',
        '--background-activated': 'var(--rf-bg-elevation-1-alt)',
        '--background-activated-opacity': '0.5',
        '--border-color': 'var(--rf-border-elevation-1-secondary)',
      },
      '1-ios': {
        '--background': 'var(--rf-bg-elevation-1-ios)',
        '--background-activated': 'var(--rf-bg-elevation-1-ios-alt)',
        '--background-activated-opacity': '0.5',
        '--border-color': 'var(--rf-border-elevation-1-ios-secondary)',
      },
    })[String(props.elevation)],
)

const accentClass = computed(
  () =>
    ({
      blue: 'text-fg-blue',
      red: 'text-fg-red',
    })[String(props.accent)],
)

const colorClasses = computed(
  () =>
    ({
      0: {
        fgPrimary: 'text-fg-elevation-0-primary',
        fgSecondary: 'text-fg-elevation-0-secondary',
        fgTertiary: 'text-fg-elevation-0-tertiary',
      },
      1: {
        fgPrimary: 'text-fg-elevation-1-primary',
        fgSecondary: 'text-fg-elevation-1-secondary',
        fgTertiary: 'text-fg-elevation-1-tertiary',
      },
      '1-ios': {
        fgPrimary: 'text-fg-elevation-1-ios-primary',
        fgSecondary: 'text-fg-elevation-1-ios-secondary',
        fgTertiary: 'text-fg-elevation-1-ios-tertiary',
      },
    })[String(props.elevation)],
)
</script>
